import { createWebHistory, createRouter } from 'vue-router';
import sessionService from '@/services/session.service';
import store from './store/global.store';
import navigationService from './services/core/navigation.service';
import { PermissionKey } from './enums/PermissionKey';

import Public from '@/views/Public';
import Login from '@/views/auth/Login';

import Root from '@/views/Root';
import Dispatcher from '@/views/Dispatcher';
import BatteryOwnership from '@/views/batteryOwnership/BatteryOwnership';
import Companies from '@/views/company/Companies';
import CompanyUsage from '@/views/companyUsage/CompanyUsage';
import DeviceUsers from '@/views/deviceUser/DeviceUsers';
import Users from '@/views/user/Users';
import UsersAdmin from '@/views/userAdmin/UsersAdmin';
import Billing from '@/views/company/Billing';
import CompanyBillingHistory from '@/views/companyBillingHistory/CompanyBillingHistory';
import CompanyBillingHistoryDetails from '@/views/companyBillingHistory/CompanyBillingHistoryDetails';
import TinyPumper from '@/views/tinyPumper/TinyPumper';
import TinyPumperHealth from '@/views/tinyPumper/TinyPumperHealth';
import TinyPumperPendingIssues from '@/views/tinyPumper/TinyPumperPendingIssues';
import DemoSettings from '@/views/demo/DemoSettings';
import SystemStatus from '@/views/systemStatus/SystemStatus';
import Integrations from '@/views/integrations/Integrations';
import OnboardingList from '@/views/onboarding/OnboardingList';
import OnboardingGearUpReview from '@/views/onboarding/OnboardingGearUpReview';
import OnboardingDetail from '@/views/onboarding/OnboardingDetail';
import RequestTrace from '@/views/requestTrace/RequestTrace';
import Diagnostics from '@/views/diagnostics/Diagnostics';
import OpsChangeInbox from '@/views/opsChangeInbox/OpsChangeInbox';
import OpsChangeInboxDetail from '@/views/opsChangeInbox/OpsChangeInboxDetail';
import SmsLookup from '@/views/smsLookup/SmsLookup.vue';
import SmsSubscriptions from '@/views/smsSubscriptions/SmsSubscriptions';

const routes = [
    {
        path: '/auth',
        redirect: '/auth/login',
        component: Public,
        children: [
            {
                path: '/auth/login',
                name: 'login',
                component: Login
            }
        ]
    },
    {
        path: '/dispatcher',
        component: Dispatcher
    },
    {
        path: '/',
        name: 'root',
        redirect: () => navigationService.getDefaultRoute(),
        component: Root,
        meta: {
            isRoot: true,
            requiresAuth: true
        },
        children: [
            {
                path: '/companies',
                component: Companies,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/users',
                component: Users,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/users-admin',
                component: UsersAdmin,
                meta: {
                    permissionKey: PermissionKey.UserAdministrator
                }
            },
            {
                path: '/devices-users',
                component: DeviceUsers,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/sms-subscriptions',
                component: SmsSubscriptions,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/sms-lookup',
                component: SmsLookup,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/company-usage',
                component: CompanyUsage
            },
            {
                path: '/request-trace',
                component: RequestTrace,
                meta: {
                    permissionKey: PermissionKey.Experimental
                }
            },
            {
                path: '/company-billing-history',
                component: CompanyBillingHistory,
                meta: {
                    permissionKey: PermissionKey.Billing
                }
            },
            {
                path: '/company-billing-history/:id',
                component: CompanyBillingHistoryDetails,
                meta: {
                    permissionKey: PermissionKey.Billing
                }
            },
            {
                path: '/billing/:companyId',
                component: Billing,
                meta: {
                    permissionKey: PermissionKey.Billing
                }
            },
            {
                path: '/battery-ownership',
                component: BatteryOwnership,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/tiny-pumper',
                component: TinyPumper,
                meta: {
                    permissionKey: PermissionKey.TinyPumper
                }
            },
            {
                path: '/tiny-pumper-health',
                component: TinyPumperHealth,
                meta: {
                    permissionKey: PermissionKey.TinyPumper
                }
            },
            {
                path: '/tiny-pumper-pending-issues',
                component: TinyPumperPendingIssues,
                meta: {
                    permissionKey: PermissionKey.TinyPumper
                }
            },
            {
                path: '/demo-settings',
                component: DemoSettings,
                meta: {
                    permissionKey: PermissionKey.Demo
                }
            },
            {
                path: '/system-status',
                component: SystemStatus,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/integrations',
                component: Integrations,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/onboarding',
                component: OnboardingList,
                meta: {
                    permissionKey: PermissionKey.GearUp
                }
            },
            {
                path: '/onboarding/gear-up/:submissionId',
                component: OnboardingGearUpReview,
                meta: {
                    permissionKey: PermissionKey.GearUp
                }
            },
            {
                path: '/onboarding/:id',
                component: OnboardingDetail,
                meta: {
                    permissionKey: PermissionKey.GearUp
                }
            },
            {
                path: '/diagnostics',
                component: Diagnostics,
                meta: {
                    allowReadOnly: true
                }
            },
            {
                path: '/ops-change-inbox',
                component: OpsChangeInbox,
                meta: {
                    allowReadOnly: true,
                    permissionKey: PermissionKey.Experimental
                }
            },
            {
                path: '/ops-change-inbox/:threadId',
                component: OpsChangeInboxDetail,
                meta: {
                    allowReadOnly: true,
                    permissionKey: PermissionKey.Experimental
                }
            }
        ]
    },
    {
        path: '/:catchAll(.*)',
        redirect: '/'
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active'
});

router.beforeEach((to, from, next) => {
    if (to.meta.requiresAuth) {
        const token = sessionService.getToken();
        if (!token) {
            window.location.href = `/auth/login`;
            return;
        }

        store.actions
            .loadTaxonomy()
            .then(() => {
                const { permissionKey, allowReadOnly } = to.meta || {};
                const hasPermissions = navigationService.routeHasPermissions(permissionKey, allowReadOnly);

                if (hasPermissions) next();
                else next(navigationService.getDefaultRoute());
            })
            .catch(() => {
                sessionService.removeToken();
                window.location.href = '/auth/login';
            });
    } else next();
});

export default router;
